// src/pages/PageMenu/CompanyDefinitions.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// أيقونات Material-UI المستوردة
import BusinessIcon from "@mui/icons-material/Business";
import PercentIcon from "@mui/icons-material/Percent";
import PaymentIcon from "@mui/icons-material/Payment";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PeopleIcon from "@mui/icons-material/People";
import QrCodeIcon from "@mui/icons-material/QrCode";

export default function CompanyDefinitions() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isRTL = i18n.language === 'ar';

  // تعريف عناصر التعريفات مع استخدام الترجمة
  const definitionItems = [
    {
      name: t('companyDefinitions.companyProfile'),
      icon: <BusinessIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#1976d2" }} />,
      link: "/company/profile",
    },
    {
      name: t('companyDefinitions.taxDefinition'),
      icon: <PercentIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#388e3c" }} />,
      link: "/vat-types",
    },
    {
      name: t('companyDefinitions.invoiceTypes'),
      icon: <DescriptionIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#d32f2f" }} />,
      link: "/invoice-types",
    },
    {
      name: t('companyDefinitions.companySettings'),
      icon: <SettingsIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#9c27b0" }} />,
      link: "/settings",
    },
    {
      name: t('companyDefinitions.branchDefinition'),
      icon: <ApartmentIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#0288d1" }} />,
      link: "/branches",
    },
    {
      name: t('companyDefinitions.users'),
      icon: <PeopleIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#303f9f" }} />,
      link: "/UserForm",
    },
    {
      name: "إعدادات الربط (ZATCA)",
      icon: <QrCodeIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56, lg: 64 }, color: "#4caf50" }} />,
      link: "/zatca-settings",
    },
  ];

  // دالة مساعدة لعرض الأيقونات داخل Grid
  const renderGridItems = (items) => (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {items.map((item, idx) => (
        <Grid
          item
          xs={6}        // جوال: عمودين
          sm={4}        // تابلت: 3
          md={3}        // لابتوب: 4
          lg={2.4}      // شاشة كبيرة: 5
          xl={2}        // شاشة أكبر: 6
          key={item.name + idx}
        >
          <ButtonBase
            onClick={() => navigate(item.link)}
            sx={{
              width: "100%",
              minHeight: { xs: 140, sm: 160, md: 180, lg: 200 },
              borderRadius: { xs: 2, sm: 2.5, md: 3 },
              border: "2px solid #e0e0e0",
              backgroundColor: "#ffffff",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: { xs: "translateY(-3px)", sm: "translateY(-4px)", md: "translateY(-5px)" },
                boxShadow: { xs: "0 6px 20px rgba(0,0,0,0.12)", sm: "0 8px 25px rgba(0,0,0,0.15)", md: "0 10px 30px rgba(0,0,0,0.18)" },
                borderColor: "#1976d2",
                backgroundColor: "#f8fafc",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
              }
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={{ xs: 2, sm: 2.5, md: 3 }}
              textAlign="center"
            >
              {item.icon}
              <Typography
                variant="body1"
                fontWeight="600"
                mt={{ xs: 1, sm: 1.5 }}
                color="text.primary"
                sx={{
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                    md: '1.1rem',
                    lg: '1.15rem'
                  },
                  lineHeight: 1.3,
                  px: 1
                }}
              >
                {item.name}
              </Typography>
            </Box>
          </ButtonBase>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header مع أزرار التنقل */}
      <Paper 
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4, md: 5 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BusinessIcon sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, color: 'white' }} />
            <Typography 
              variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
              fontWeight="bold" 
              color="white"
              sx={{
                fontSize: {
                  xs: '1.5rem',
                  sm: '2rem',
                  md: '2.5rem'
                },
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              {t('companyDefinitions.title')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* قسم التعريفات الرئيسية */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #f3e5f5',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #9c27b0',
            paddingBottom: 1.5,
            color: '#7b1fa2',
          }}
        >
          ⚙️ {t('companyDefinitions.sectionTitle')}
        </Typography>
        {renderGridItems(definitionItems)}
      </Paper>
    </Container>
  );
}